import { useState } from "react";
import { useResource } from "@/hooks/useResource";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { RoleBadge } from "@/components/common/RoleBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import api, { apiErrorMessage } from "@/lib/api";
import { ROLE_LABELS, ALL_ROLES } from "@/lib/rbac";
import { toast } from "sonner";
import { Plus, Pencil, KeyRound, UserX, UserCheck, Loader2, Search, Trash2 } from "lucide-react";

const emptyForm = { name: "", email: "", password: "", role: "", department: "", mobile: "" };

const selCls = "h-9 w-full rounded-md border border-stone-200 bg-white px-3 text-sm";

function RoleSelect({ value, onChange, disabled, testId }) {
  return (
    <select data-testid={testId} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} className={selCls}>
      <option value="">Select role…</option>
      {ALL_ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
    </select>
  );
}

export default function Users() {
  const { user } = useAuth();
  const { data: raw, refetch } = useResource("/admin/users");
  const users = (Array.isArray(raw) ? raw : []).slice().sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  const [q, setQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [resetTarget, setResetTarget] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [busy, setBusy] = useState(false);

  const filtered = users.filter((u) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return [u.name, u.email, u.department, u.mobile, ROLE_LABELS[u.role] || u.role].some((v) => String(v || "").toLowerCase().includes(s));
  });

  const create = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (!form.email.trim()) return toast.error("Email is required");
    if (!form.role) return toast.error("Select a role");
    if (form.password.length < 8) return toast.error("Password must be at least 8 characters");
    setBusy(true);
    try {
      await api.post("/admin/users", { ...form, email: form.email.trim(), name: form.name.trim() });
      toast.success(`User ${form.email.trim()} created`);
      setCreateOpen(false); setForm(emptyForm); refetch();
    } catch (e) { toast.error(apiErrorMessage(e, "Create failed")); } finally { setBusy(false); }
  };

  const openEdit = (u) => { setEditTarget(u); setEditForm({ name: u.name || "", email: u.email || "", role: u.role || "", department: u.department || "", mobile: u.mobile || "" }); };

  const saveEdit = async () => {
    if (!editForm.name.trim()) return toast.error("Name is required");
    if (!editForm.email.trim()) return toast.error("Email is required");
    if (!editForm.role) return toast.error("Select a role");
    setBusy(true);
    try {
      await api.put(`/admin/users/${editTarget.id}`, editForm);
      toast.success("User updated");
      setEditTarget(null); refetch();
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); } finally { setBusy(false); }
  };

  const resetPw = async () => {
    if (newPw.length < 8) return toast.error("Password must be at least 8 characters");
    setBusy(true);
    try {
      await api.put(`/admin/users/${resetTarget.id}/password`, { password: newPw });
      toast.success(`Password reset for ${resetTarget.email}`);
      setResetTarget(null); setNewPw("");
    } catch (e) { toast.error(apiErrorMessage(e, "Reset failed")); } finally { setBusy(false); }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/admin/users/${u.id}`, { active: !u.active });
      toast.success(u.active ? `${u.name} deactivated` : `${u.name} activated`);
      refetch();
    } catch (e) { toast.error(apiErrorMessage(e, "Action failed")); }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);

  const deleteUser = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/users/${deleteTarget.id}`);
      toast.success(`User ${deleteTarget.email} removed`);
      setDeleteTarget(null); refetch();
    } catch (e) { toast.error(apiErrorMessage(e, "Remove failed")); } finally { setBusy(false); }
  };

  const isSelf = (u) => u.id === user?.id || u.email === user?.email;

  return (
    <div className="space-y-6">
      <PageHeader title="Users & Roles" subtitle="Create users · assign roles · reset passwords · Super Admin only">
        <Button data-testid="users-new-btn" className="gap-2 bg-amber-700 hover:bg-amber-800" onClick={() => { setForm(emptyForm); setCreateOpen(true); }}>
          <Plus className="h-4 w-4" /> New User
        </Button>
      </PageHeader>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
        <Input data-testid="users-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, department, role…" className="h-9 pl-9" />
      </div>

      <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white shadow-sm" data-testid="users-table">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-left text-xs uppercase text-stone-500">
            <tr>{["Name", "Email", "Role", "Department", "Mobile", "Status", "Last Login", ""].map((h) => <th key={h} className="px-3 py-2.5">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {filtered.length === 0 ? (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-stone-400">No users found.</td></tr>
            ) : filtered.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.email}`} className="hover:bg-stone-50/60">
                <td className="px-3 py-2 font-medium text-stone-800">{u.name}{isSelf(u) && <span className="ml-2 rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-semibold text-stone-500">YOU</span>}</td>
                <td className="px-3 py-2 text-stone-600">{u.email}</td>
                <td className="px-3 py-2"><RoleBadge role={u.role} /></td>
                <td className="px-3 py-2 text-stone-600">{u.department || "—"}</td>
                <td className="px-3 py-2 text-stone-600">{u.mobile || "—"}</td>
                <td className="px-3 py-2">
                  <span data-testid={`user-status-${u.email}`} className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${u.active !== false ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-stone-200 bg-stone-100 text-stone-500"}`}>
                    {u.active !== false ? "ACTIVE" : "INACTIVE"}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs text-stone-500">{u.lastLogin ? new Date(u.lastLogin).toLocaleString("en-IN") : "—"}</td>
                <td className="px-3 py-1.5">
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-stone-500" title="Edit" data-testid={`user-edit-${u.email}`} onClick={() => openEdit(u)}><Pencil className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-stone-500" title="Reset password" data-testid={`user-reset-${u.email}`} onClick={() => { setResetTarget(u); setNewPw(""); }}><KeyRound className="h-4 w-4" /></Button>
                    {!isSelf(u) && (
                      <Button size="sm" variant="ghost" className={`h-7 px-2 ${u.active !== false ? "text-rose-500" : "text-emerald-600"}`} title={u.active !== false ? "Deactivate" : "Activate"} data-testid={`user-toggle-${u.email}`} onClick={() => toggleActive(u)}>
                        {u.active !== false ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                      </Button>
                    )}
                    {!isSelf(u) && (
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-rose-600 hover:text-rose-700" title="Remove user permanently" data-testid={`user-delete-${u.email}`} onClick={() => setDeleteTarget(u)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create user */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-white sm:max-w-lg" data-testid="user-create-dialog">
          <DialogHeader>
            <DialogTitle>New User</DialogTitle>
            <DialogDescription className="text-xs">Set an initial password and share it with the user. They can log in immediately.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Full Name *</Label><Input data-testid="user-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-9" /></div>
              <div><Label className="text-xs">Email *</Label><Input type="email" data-testid="user-form-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-9" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Role *</Label><RoleSelect testId="user-form-role" value={form.role} onChange={(v) => setForm({ ...form, role: v })} /></div>
              <div><Label className="text-xs">Initial Password * (min 8 chars)</Label><Input type="password" data-testid="user-form-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-9" autoComplete="new-password" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Department</Label><Input data-testid="user-form-department" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="h-9" /></div>
              <div><Label className="text-xs">Mobile</Label><Input data-testid="user-form-mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="h-9" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button data-testid="user-create-save" className="gap-2 bg-amber-700 hover:bg-amber-800" disabled={busy} onClick={create}>
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit user */}
      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="bg-white sm:max-w-lg" data-testid="user-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit User {editTarget ? `— ${editTarget.name}` : ""}</DialogTitle>
            <DialogDescription className="text-xs">Update details and role assignment.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Full Name *</Label><Input data-testid="user-edit-name" value={editForm.name || ""} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="h-9" /></div>
              <div><Label className="text-xs">Email *</Label><Input type="email" data-testid="user-edit-email" value={editForm.email || ""} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="h-9" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Role *</Label>
                <RoleSelect testId="user-edit-role" value={editForm.role || ""} disabled={editTarget && isSelf(editTarget)} onChange={(v) => setEditForm({ ...editForm, role: v })} />
                {editTarget && isSelf(editTarget) && <p className="mt-1 text-[11px] text-amber-700">You cannot change your own role.</p>}
              </div>
              <div><Label className="text-xs">Department</Label><Input data-testid="user-edit-department" value={editForm.department || ""} onChange={(e) => setEditForm({ ...editForm, department: e.target.value })} className="h-9" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Mobile</Label><Input data-testid="user-edit-mobile" value={editForm.mobile || ""} onChange={(e) => setEditForm({ ...editForm, mobile: e.target.value })} className="h-9" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)}>Cancel</Button>
            <Button data-testid="user-edit-save" className="gap-2 bg-amber-700 hover:bg-amber-800" disabled={busy} onClick={saveEdit}>
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password */}
      <Dialog open={!!resetTarget} onOpenChange={(o) => { if (!o) { setResetTarget(null); setNewPw(""); } }}>
        <DialogContent className="bg-white sm:max-w-md" data-testid="user-reset-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound className="h-4 w-4 text-amber-700" /> Reset Password</DialogTitle>
            <DialogDescription className="text-xs">Set a new password for {resetTarget?.email}. Their current sessions remain valid until token expiry.</DialogDescription>
          </DialogHeader>
          <div><Label className="text-xs">New Password (min 8 chars)</Label><Input type="password" data-testid="user-reset-password" value={newPw} onChange={(e) => setNewPw(e.target.value)} className="h-9" autoComplete="new-password" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResetTarget(null); setNewPw(""); }}>Cancel</Button>
            <Button data-testid="user-reset-save" className="gap-2 bg-amber-700 hover:bg-amber-800" disabled={busy} onClick={resetPw}>
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove user */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-white" data-testid="user-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><Trash2 className="h-5 w-5 text-rose-600" /> Remove user permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.name} ({deleteTarget?.email}) will be permanently removed and will no longer be able to log in. This cannot be undone. To keep the account but block access, use Deactivate instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="user-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="user-delete-confirm" className="gap-2 bg-rose-600 hover:bg-rose-700" disabled={busy} onClick={deleteUser}>
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Remove User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
